import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Fingerprint, Plus, RefreshCw, Search, ShieldAlert, Eye, ScrollText } from "lucide-react";
import { formatNotes, hasVisibleNotes } from "@/lib/safeDisplay";

// ---------------------------------------------------------------------------
// MP12 Module 6A — Restricted Identity Desk.
// Access is limited by the server to super_admin + gfmp_reviewer. Any
// unauthorised viewer receives a 403 (no redacted preview is shown —
// restricted identity mappings are never previewed to unauthorised roles).
// ---------------------------------------------------------------------------

function canAccessIdentity(user) {
  return !!user && ["super_admin", "gfmp_reviewer"].includes(user.role);
}

function NewIdentityDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    cisn: "", cis_idn: "", internal_reference: "",
    actual_client_name: "", actual_contact_email: "", actual_contact_phone: "",
    actual_client_address: "", identity_notes: "",
  });
  const submit = async () => {
    if (!form.actual_client_name.trim()) { toast.error("Actual client name is required."); return; }
    setSaving(true);
    try {
      const r = await api.post("/restricted-identity", form);
      onCreated?.(r.data);
      toast.success("Identity mapping created.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="ri-id-new" className="gap-2"><Plus className="w-4 h-4" /> New identity mapping</Button></DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New Restricted Identity Mapping</DialogTitle>
          <DialogDescription>Creates a broadly-usable reference (CISN / CIS-IDN / internal ref) mapped to the actual client identity. Access is limited to Super Admin and GFMP Reviewer.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>CISN</Label><Input value={form.cisn} onChange={(e)=>setForm({...form, cisn: e.target.value})} data-testid="ri-id-cisn" /></div>
          <div><Label>CIS-IDN</Label><Input value={form.cis_idn} onChange={(e)=>setForm({...form, cis_idn: e.target.value})} data-testid="ri-id-cis-idn" /></div>
          <div className="col-span-2"><Label>Internal reference</Label><Input value={form.internal_reference} onChange={(e)=>setForm({...form, internal_reference: e.target.value})} placeholder="bespoke internal ref" /></div>
          <div className="col-span-2"><Label>Actual client name <span className="text-destructive">*</span></Label><Input value={form.actual_client_name} onChange={(e)=>setForm({...form, actual_client_name: e.target.value})} data-testid="ri-id-name" /></div>
          <div><Label>Contact email</Label><Input value={form.actual_contact_email} onChange={(e)=>setForm({...form, actual_contact_email: e.target.value})} /></div>
          <div><Label>Contact phone</Label><Input value={form.actual_contact_phone} onChange={(e)=>setForm({...form, actual_contact_phone: e.target.value})} /></div>
          <div className="col-span-2"><Label>Client address</Label><Textarea rows={2} value={form.actual_client_address} onChange={(e)=>setForm({...form, actual_client_address: e.target.value})} /></div>
          <div className="col-span-2"><Label>Identity notes</Label><Textarea rows={2} value={form.identity_notes} onChange={(e)=>setForm({...form, identity_notes: e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ri-id-save">{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RestrictedIdentityDesk() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [lookupToken, setLookupToken] = useState("");
  const [lookupField, setLookupField] = useState("cisn");
  const [lookupResult, setLookupResult] = useState(null);
  const [expandedLog, setExpandedLog] = useState({});
  const [logRows, setLogRows] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/restricted-identity", { params });
      setRows(r.data?.rows || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Load failed.");
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { if (canAccessIdentity(user)) load(); else setLoading(false); }, [user, load]);

  const doLookup = async () => {
    if (!lookupToken.trim()) { toast.error("Enter a token to look up."); return; }
    setLookupResult(null);
    try {
      const r = await api.get("/restricted-identity/lookup", { params: { [lookupField]: lookupToken.trim() } });
      setLookupResult(r.data);
      toast.success("Match resolved. Access logged to Proof Ledger.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No match.");
    }
  };

  const toggleLog = async (rid) => {
    const next = !expandedLog[rid];
    setExpandedLog({ ...expandedLog, [rid]: next });
    if (next && !logRows[rid]) {
      try {
        const r = await api.get(`/restricted-identity/${rid}/access-log`, { params: { limit: 50 } });
        setLogRows({ ...logRows, [rid]: r.data?.rows || [] });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Log fetch failed.");
      }
    }
  };

  if (!canAccessIdentity(user)) {
    return (
      <div className="p-8">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Restricted Identity Desk</CardTitle></CardHeader>
          <CardContent><p className="text-sm text-muted-foreground">Access to restricted identity mappings is limited to Super Admin and GFMP Reviewer. This attempt has been recorded.</p></CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="restricted-identity-desk">
      <div className="flex flex-wrap items-center gap-3">
        <Fingerprint className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Restricted Identity Desk</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            CISN / CIS-IDN / internal reference → actual client identity resolution. Every read is audited into the Proof Ledger. Never surfaced to beta testers, industry reviewers, or public users.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">Internal-only</Badge>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="ri-id-refresh"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        <NewIdentityDialog onCreated={(x)=>setRows((r)=>[x, ...r])} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Lookup</CardTitle><CardDescription>Every successful lookup writes an audit row.</CardDescription></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Field</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={lookupField} onChange={(e)=>setLookupField(e.target.value)} data-testid="ri-id-lookup-field">
                <option value="cisn">CISN</option>
                <option value="cis_idn">CIS-IDN</option>
                <option value="internal_reference">Internal reference</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Token</Label>
              <Input value={lookupToken} onChange={(e)=>setLookupToken(e.target.value)} data-testid="ri-id-lookup-token" />
            </div>
            <div className="flex items-end">
              <Button onClick={doLookup} className="w-full gap-2" data-testid="ri-id-lookup-go"><Eye className="w-4 h-4" /> Look up</Button>
            </div>
          </div>
          {lookupResult && (
            <div className="mt-4 border border-border rounded-md p-3 text-sm" data-testid="ri-id-lookup-result">
              <div className="font-semibold">{lookupResult.actual_client_name}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs mt-2">
                <div><span className="opacity-60">CISN:</span> {lookupResult.cisn || "—"}</div>
                <div><span className="opacity-60">CIS-IDN:</span> {lookupResult.cis_idn || "—"}</div>
                <div><span className="opacity-60">Internal ref:</span> {lookupResult.internal_reference || "—"}</div>
                <div><span className="opacity-60">Email:</span> {lookupResult.actual_contact_email || "—"}</div>
                <div><span className="opacity-60">Phone:</span> {lookupResult.actual_contact_phone || "—"}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> All mappings ({rows.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="mb-3">
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="cisn, cis-idn, ref, name, email…" onBlur={load} data-testid="ri-id-search" />
          </div>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
           rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="ri-id-empty">No mappings recorded.</p> :
           <div className="space-y-2">
             {rows.map((r) => (
               <div key={r.id} className="border border-border rounded-md" data-testid={`ri-id-row-${r.id}`}>
                 <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                   <span className="font-mono text-xs opacity-70">{r.cisn || "—"}</span>
                   <span className="font-mono text-xs opacity-70">{r.cis_idn || "—"}</span>
                   <span className="font-semibold text-sm">{r.actual_client_name}</span>
                   {r.active ? <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]" style={{ background: "hsl(150 45% 92%)", color: "hsl(150 45% 24%)", borderColor: "hsl(150 40% 66%)" }}>Active</Badge> : <Badge variant="outline" className="text-[10px]">Deactivated</Badge>}
                   <div className="flex-1" />
                   <Button variant="outline" size="sm" className="gap-1" onClick={()=>toggleLog(r.id)} data-testid={`ri-id-log-${r.id}`}><ScrollText className="w-3.5 h-3.5" /> Access log</Button>
                 </div>
                 {expandedLog[r.id] && (
                   <div className="border-t border-border px-4 py-3 text-xs">
                     {(logRows[r.id] || []).length === 0 ? <span className="opacity-60">No audit rows recorded.</span> :
                       <ul className="space-y-1">
                         {(logRows[r.id] || []).map((row) => (
                           <li key={row.audit_id}><span className="font-mono opacity-60">{row.timestamp}</span> · {row.action_type} · {row.user_email}{hasVisibleNotes(row.notes) ? ` — ${formatNotes(row.notes, { fallback: "" })}` : ""}</li>
                         ))}
                       </ul>
                     }
                   </div>
                 )}
               </div>
             ))}
           </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}
